/**
 * The file sidebar behind a verification document box (Credit auth / Credit
 * report / Background check on an ApplicantCard) — ported from
 * tpo-portal-frontend's ChecklistUploadSidebar, on this repo's panel-form
 * tokens (FORM_STYLE_GUIDE.md: formSx.sidebarHead / sidebarBody, FormSection,
 * FormPanelFooter).
 *
 * Two modes, one layout, because both end the same way — a file filed as the
 * verification's type and linked to the applicant:
 *   • upload — files the user just picked or dropped. Each is renameable and
 *     goes up through `upload_loan_docs_with_associations`, typed and linked to
 *     the applicant in one request.
 *   • assign — one document already on the loan. It can be renamed (written
 *     with `update_loan_doc_metadata`) and is then linked to the applicant.
 *
 * Name suggestions (owner, 2026-10-01):
 *   `{Document type} - {today M.d.yy, Eastern} - {Applicant full name}`
 *   `{Document type} - {Applicant full name}`
 * A suggestion never collides: when a loan document, or another file in this
 * batch, already carries it, it takes the next free ` (n)`.
 */
import CloseRounded from "@mui/icons-material/CloseRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import LinkRounded from "@mui/icons-material/LinkRounded";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import {
  FormPanelFooter,
  FormSection,
} from "../../../components/forms/FormPrimitives";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { slate, status, teal } from "../../../theme/colors";
import { FORM, formSx } from "../../../theme/formStyles";

const IMAGE_EXTS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "tif",
  "tiff",
  "bmp",
];

/* ── name helpers ──────────────────────────────────────── */

const extOf = (name) => {
  const m = String(name || "").match(/\.([A-Za-z0-9]{1,6})$/);
  return m ? m[1] : "";
};
const stripExt = (name) =>
  String(name || "").replace(/\.[A-Za-z0-9]{1,6}$/, "");
const nameKey = (name) => stripExt(name).trim().toLowerCase();

const fileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const kb = bytes / 1024;
  if (kb < 1) return `${bytes} B`;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

/** First free `base`, `base (2)`, `base (3)` … not in `taken`. */
const freeName = (base, taken) => {
  if (!taken.has(nameKey(base))) return base;
  for (let n = 2; n < 100; n += 1) {
    const next = `${base} (${n})`;
    if (!taken.has(nameKey(next))) return next;
  }
  return base;
};

export const buildVerificationNameSuggestions = ({
  docType,
  fullName,
  taken,
}) => {
  if (!docType) return [];
  const today = formatDateTime(new Date().toISOString(), "M.d.yy");
  const bases = [
    fullName && today ? `${docType} - ${today} - ${fullName}` : null,
    fullName ? `${docType} - ${fullName}` : `${docType}`,
  ].filter(Boolean);
  return bases.map((b) => freeName(b, taken));
};

/* ── pieces ────────────────────────────────────────────── */

const FileBadge = ({ name, size }) => {
  const ext = extOf(name).toLowerCase();
  const tone =
    ext === "pdf"
      ? { bg: status.error.bg, fg: status.error.fg }
      : IMAGE_EXTS.includes(ext)
        ? { bg: "#F5F3FF", fg: "#6D28D9" }
        : { bg: slate[100], fg: slate[600] };
  return (
    <Flx
      column
      sx={{ alignItems: "center", gap: 0.5, width: 44, flexShrink: 0 }}
    >
      <Flx
        ac
        jc
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: tone.bg,
          color: tone.fg,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".02em",
        }}
      >
        {(ext || "file").slice(0, 4).toUpperCase()}
      </Flx>
      {size != null && (
        <Txt sx={{ ...formSx.meta, fontSize: 11, whiteSpace: "nowrap" }}>
          {fileSize(size)}
        </Txt>
      )}
    </Flx>
  );
};

const SuggestionButton = ({ name, onPick }) => (
  <ButtonBase
    onClick={() => onPick(name)}
    sx={{
      justifyContent: "flex-start",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 0.875,
      borderRadius: "8px",
      border: `1px solid ${FORM.color.accentBorder}`,
      backgroundColor: FORM.color.accentTint,
      textAlign: "left",
      "&:hover": { backgroundColor: FORM.color.accentWash },
    }}
  >
    <Txt
      sx={{ fontSize: 12, fontWeight: 700, color: teal[700], flexShrink: 0 }}
    >
      Suggested
    </Txt>
    <Txt
      noWrap
      sx={{ fontSize: 12.5, color: FORM.color.ink2, minWidth: 0, flex: 1 }}
    >
      {name}
    </Txt>
  </ButtonBase>
);

const INLINE_LINK_SX = {
  ...formSx.link,
  flexShrink: 0,
  fontSize: 12,
  borderRadius: "4px",
  "&:hover": { textDecoration: "underline" },
};

/**
 * One file: badge · editable name (extension fixed in an adornment) ·
 * remove. "Renamed from … · Undo" once the name changes; the suggestions step
 * aside once one is taken and come back on Undo.
 */
const FileRow = ({
  entry,
  suggestions,
  onPick,
  onRename,
  onUndo,
  onRemove,
  onSubmit,
  autoFocus,
  last,
  removable,
  filedAs,
}) => {
  const ext = extOf(entry.name);
  const current = stripExt(entry.file_display_name);
  const renamed = entry.file_display_name !== entry.name;
  const withExt = (base) => (ext ? `${base}.${ext}` : base);
  const offered = entry.suggestionApplied
    ? []
    : suggestions.filter((s) => s !== current);

  return (
    <Flx
      g={1.5}
      sx={{
        alignItems: "flex-start",
        py: 2,
        "&:first-of-type": { pt: 0 },
        borderBottom: last ? "none" : `1px solid ${FORM.color.divider}`,
      }}
    >
      <FileBadge name={entry.name} size={entry.size} />
      <Flx column g={0.75} sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus={autoFocus}
          value={current}
          inputProps={{ "aria-label": "File name" }}
          onChange={(e) => onRename(withExt(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
          InputProps={{
            endAdornment: ext ? (
              <InputAdornment
                position="end"
                sx={{
                  alignSelf: "stretch",
                  maxHeight: "none",
                  height: "auto",
                  ml: 0,
                  pl: 1.5,
                  borderLeft: `1px solid ${FORM.color.divider}`,
                  "& .MuiTypography-root": {
                    fontSize: 13,
                    color: FORM.color.ink3,
                  },
                }}
              >
                .{ext}
              </InputAdornment>
            ) : null,
          }}
          sx={formSx.input}
        />
        {renamed && (
          <Flx ac g={1} sx={{ minWidth: 0 }}>
            <Txt noWrap sx={{ ...formSx.meta, minWidth: 0 }}>
              Renamed from {entry.name}
            </Txt>
            <ButtonBase onClick={onUndo} sx={INLINE_LINK_SX}>
              Undo
            </ButtonBase>
          </Flx>
        )}
        {filedAs && (
          <Txt noWrap sx={{ ...formSx.meta, minWidth: 0, color: teal[700] }}>
            Files as {filedAs}
          </Txt>
        )}
        {entry.meta && (
          <Txt noWrap sx={{ ...formSx.meta, minWidth: 0 }}>
            {entry.meta}
          </Txt>
        )}
        {offered.map((name) => (
          <SuggestionButton key={name} name={name} onPick={onPick} />
        ))}
      </Flx>
      {removable && (
        <Tooltip title="Remove">
          <IconButton
            size="small"
            aria-label={`Remove ${entry.name}`}
            onClick={onRemove}
            sx={{
              mt: 0.5,
              color: FORM.color.ink3,
              "&:hover": { color: FORM.color.ink },
            }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Flx>
  );
};

/* ── the sidebar ───────────────────────────────────────── */

const VerificationUploadSidebar = ({
  open,
  mode,
  files,
  doc,
  label,
  target,
  altTargets,
  borrower,
  loan,
  onClose,
  onDone,
}) => {
  const dispatch = useDispatch();
  const {
    uploadLoanDocsWithAssociations,
    updateLoanDocMetadata,
    associateDocumentWithObjects,
  } = useLoanFilesHook();
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const isAssign = mode === "assign";

  const fullName = [borrower?.firstName, borrower?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  // Reset whenever the sidebar opens on new input.
  useEffect(() => {
    if (!open) return;
    setBusy(false);
    if (isAssign && doc) {
      const name = doc.file_display_name || doc.name || "Untitled document";
      setEntries([
        {
          id: String(doc._id),
          name,
          file_display_name: name,
          size: doc.size ?? null,
          meta: [
            doc.docType,
            doc.uploadDate
              ? `Uploaded ${formatDateTime(doc.uploadDate, "date")}`
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
        },
      ]);
    } else {
      setEntries(files || []);
    }
  }, [open, isAssign, doc, files]);

  // Every loan document's name except the one being assigned (renaming it to
  // its own name is not a collision).
  const loanNameKeys = useMemo(
    () =>
      (loan?.loanDocuments || [])
        .filter((d) => !(isAssign && String(d?._id) === String(doc?._id)))
        .map((d) => nameKey(d?.file_display_name || d?.name))
        .filter(Boolean),
    [loan?.loanDocuments, isAssign, doc?._id],
  );

  const suggestions = useMemo(() => {
    const out = {};
    entries.forEach((entry) => {
      const taken = new Set(loanNameKeys);
      entries.forEach((other) => {
        if (other.id !== entry.id) taken.add(nameKey(other.file_display_name));
      });
      // The item's own type first, then each alternative (the Credit Auth /
      // Loan Application box also offers Loan Application names). A picked
      // name files the upload as THAT type — byName maps it back.
      const names = [];
      const byName = new Map();
      [target, ...(altTargets || [])].filter(Boolean).forEach((tg) => {
        buildVerificationNameSuggestions({
          docType: tg.docType,
          fullName,
          taken,
        }).forEach((n) => {
          if (byName.has(n)) return;
          byName.set(n, tg);
          names.push(n);
        });
      });
      out[entry.id] = { names, byName };
    });
    return out;
  }, [entries, loanNameKeys, target, altTargets, fullName]);

  const count = entries.length;
  const filesLabel = `${count} file${count === 1 ? "" : "s"}`;
  const canSubmit = count > 0 && !busy;

  const setEntry = (id, patch) =>
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...patch } : e)),
    );
  const withExtOf = (entry, base) => {
    const ext = extOf(entry.name);
    return ext ? `${base}.${ext}` : base;
  };

  const done = (updatedLoan) => {
    if (updatedLoan?._id) dispatch(loanDrilldownSet(updatedLoan));
    setBusy(false);
    onDone?.(updatedLoan);
    onClose();
  };

  const submit = () => {
    if (!canSubmit || !loan?._id) return;
    setBusy(true);
    const fail = () => setBusy(false);

    if (isAssign) {
      const entry = entries[0];
      const link = () =>
        associateDocumentWithObjects({
          dealId: loan._id,
          loanDocumentsIds: [entry.id],
          borrowerIds: borrower?._id ? [borrower._id] : undefined,
          onSuccessFn: done,
          onFailFn: fail,
        });
      if (entry.file_display_name !== entry.name) {
        updateLoanDocMetadata({
          loanId: loan._id,
          data: [{ _id: entry.id, file_display_name: entry.file_display_name }],
          onSuccessFn: link,
          onFailFn: fail,
        });
      } else {
        link();
      }
      return;
    }

    uploadLoanDocsWithAssociations({
      loanId: loan._id,
      files: entries.map((e) => ({
        id: e.id,
        file: e.file,
        name: e.name,
        file_display_name: e.file_display_name,
        // A name picked from an alternative type files it as that type.
        docGroup: e.docGroup || target?.docGroup,
        docType: e.docType || target?.docType,
      })),
      borrowerIds: borrower?._id ? [borrower._id] : undefined,
      onSuccessMsg:
        count > 1
          ? `${count} files uploaded to ${label}`
          : `Uploaded to ${label}`,
      onSuccessFn: done,
      onFailFn: fail,
    });
  };

  const verb = isAssign ? "Assign" : "Upload";

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={busy ? undefined : onClose}
      // Clicks inside the portalled drawer still bubble through the React
      // tree to the ApplicantCard, which would open the borrower sidebar.
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      // No dimmed backdrop; the panel's own shadow sets it apart.
      hideBackdrop
      // Named so a verification popover treats presses in here as its own.
      className="verification-upload-drawer"
      // In front of the drilldown's docked sidebars (ResizeableSidebar sits
      // at 13000) and the verification popover (13400), and of the floating
      // chat button (1250).
      sx={{ zIndex: 13600 }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600 },
          display: "flex",
          flexDirection: "column",
          boxShadow: "-12px 0 32px rgba(15,23,42,.14)",
          borderLeft: `1px solid ${slate[200]}`,
        },
      }}
    >
      {/* Header */}
      <Flx ac jb g={1.5} sx={formSx.sidebarHead}>
        <Flx ac g={1.5} sx={{ minWidth: 0 }}>
          <Flx
            ac
            jc
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              flexShrink: 0,
              backgroundColor: FORM.color.accentWash,
              color: teal[700],
            }}
          >
            {isAssign ? (
              <LinkRounded sx={{ fontSize: 19 }} />
            ) : (
              <FileUploadOutlined sx={{ fontSize: 19 }} />
            )}
          </Flx>
          <Flx column sx={{ minWidth: 0 }}>
            <Txt noWrap sx={formSx.title}>
              {`${verb} to ${label}`}
            </Txt>
            <Txt noWrap sx={{ ...formSx.titleHelper, mt: 0.25 }}>
              {[fullName || "Applicant", target?.docType, filesLabel]
                .filter(Boolean)
                .join(" · ")}
            </Txt>
          </Flx>
        </Flx>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={busy}
          sx={{ color: FORM.color.ink3 }}
        >
          <CloseRounded />
        </IconButton>
      </Flx>

      {/* Body */}
      <Box sx={{ ...formSx.sidebarBody, flex: 1, overflowY: "auto" }}>
        <FormSection
          title="Files"
          helper={
            isAssign
              ? `Rename the file before it is assigned, or take a suggested name. It is filed as ${target?.docType || "this verification"} for ${fullName || "this applicant"}.`
              : `Rename a file before it goes up, or take a suggested name. It is filed as ${target?.docType || "this verification"} and assigned to ${fullName || "this applicant"}.`
          }
        >
          <Box>
            {entries.map((entry, i) => (
              <FileRow
                key={entry.id}
                entry={entry}
                suggestions={suggestions[entry.id]?.names || []}
                filedAs={
                  entry.docType && entry.docType !== target?.docType
                    ? entry.docType
                    : null
                }
                onPick={(name) => {
                  const tg = suggestions[entry.id]?.byName.get(name) || target;
                  setEntry(entry.id, {
                    file_display_name: withExtOf(entry, name),
                    suggestionApplied: true,
                    docGroup: tg?.docGroup,
                    docType: tg?.docType,
                  });
                }}
                onRename={(next) =>
                  setEntry(entry.id, {
                    file_display_name: next.trim() ? next : entry.name,
                  })
                }
                onUndo={() =>
                  setEntry(entry.id, {
                    file_display_name: entry.name,
                    suggestionApplied: false,
                    docGroup: undefined,
                    docType: undefined,
                  })
                }
                onRemove={() => {
                  const next = entries.filter((e) => e.id !== entry.id);
                  if (!next.length) onClose();
                  else setEntries(next);
                }}
                onSubmit={submit}
                autoFocus={i === 0}
                last={i === entries.length - 1}
                removable={!isAssign}
              />
            ))}
          </Box>
        </FormSection>
      </Box>

      {/* Footer */}
      <FormPanelFooter
        readyText={
          isAssign ? "Ready to assign" : `${filesLabel} ready to upload`
        }
      >
        <Button
          variant="outlined"
          color="gray"
          onClick={onClose}
          disabled={busy}
          sx={formSx.button}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          startIcon={
            busy ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={formSx.button}
        >
          {busy
            ? isAssign
              ? "Assigning…"
              : "Uploading…"
            : isAssign
              ? "Assign file"
              : `Upload ${filesLabel}`}
        </Button>
      </FormPanelFooter>
    </Drawer>
  );
};

VerificationUploadSidebar.propTypes = {
  open: PropTypes.bool,
  /** "upload" (new files) | "assign" (a document already on the loan). */
  mode: PropTypes.oneOf(["upload", "assign"]),
  /** upload: `[{ id, file, name, file_display_name, size }]`. */
  files: PropTypes.array,
  /** assign: the loan document. */
  doc: PropTypes.object,
  /** The verification's label, e.g. "Credit report". */
  label: PropTypes.string,
  /** Other `{ docGroup, docType }`s whose names are also suggested. */
  altTargets: PropTypes.arrayOf(PropTypes.object),
  /** `{ docGroup, docType }` the file is filed under. */
  target: PropTypes.shape({
    docGroup: PropTypes.string,
    docType: PropTypes.string,
  }),
  borrower: PropTypes.object,
  loan: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  /** After a successful upload / assign, with the refreshed loan. */
  onDone: PropTypes.func,
};

export default VerificationUploadSidebar;
